// Copyright 2025, University of Maryland, All Rights Reserved
import { useState, useEffect } from 'react';
import { Edit3, Trash2, CheckCircle, XCircle, Plus, AlertCircle } from 'lucide-react';
import { useAuth } from '@umd-dit-ai-workspace/workspaces-component';
import { formatDate } from '../lib/utils';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Input,
  Label,
  Textarea,
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@umd-dit-ai-workspace/shared-ui';
import { useConfigMutations } from '../hooks/useConfigMutations';
import type { TemplateResponse, ConfigurationLevel } from '../types/config';

interface TemplateEditorProps {
  level: ConfigurationLevel;
  selectedTemplate: TemplateResponse | null;
  onTemplateChange: (template: TemplateResponse | null) => void;
  onTemplateCreated?: (name: string) => void;
}

type EditorMode = 'view' | 'edit' | 'create';

export function TemplateEditor({ level, selectedTemplate, onTemplateChange, onTemplateCreated }: TemplateEditorProps) {
  // Default to create mode when no template is selected
  const [mode, setMode] = useState<EditorMode>(!selectedTemplate ? 'create' : 'view');
  const [editedContent, setEditedContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');

  const { selectedOrganizationId, selectedWorkspaceId } = useAuth();

  const mutations = useConfigMutations({
    level,
    orgId: level === 'workspace' ? selectedOrganizationId || undefined : undefined,
    wsId: level === 'workspace' ? selectedWorkspaceId || undefined : undefined,
  });

  // Initialize edit content when template changes
  useEffect(() => {
    // When a template is selected, set to view mode
    if (selectedTemplate) {
      setMode('view');
      if ('content' in selectedTemplate) {
        setEditedContent(
          typeof selectedTemplate.content === 'object'
            ? JSON.stringify(selectedTemplate.content, null, 2)
            : ''
        );
      }
    }
    // When no template is selected, default to create mode
    else {
      setMode('create');
    }
  }, [selectedTemplate]);

  // Reset create form or initialize with default template
  useEffect(() => {
    if (mode === 'create') {
      const defaultTemplate = {
        document_type: 'new-template',
        analysis_focus: ['concept_coverage', 'difficulty_assessment'],
        output_format: 'structured_html',
        llm_model: 'sonnet4',
        cost_category: 'academic_analysis'
      };
      setEditedContent(JSON.stringify(defaultTemplate, null, 2));
      setNewTemplateName('');
    }
  }, [mode]);

  const handleNew = () => {
    setMode('create');
    onTemplateChange(null);
  };

  const handleCreate = () => {
    try {
      const parsedContent = JSON.parse(editedContent);
      const templateName = newTemplateName.trim();

      mutations.createTemplate.mutate(
        {
          name: templateName,
          content: parsedContent,
        },
        {
          onSuccess: () => {
            // After successful creation, notify parent to fetch and select the new template
            onTemplateCreated?.(templateName);
          }
        }
      );
    } catch (e) {
      alert('Invalid JSON format. Please check your syntax.');
    }
  };

  const handleSave = async () => {
    if (!selectedTemplate) return;
    setIsSaving(true);
    try {
      const parsedContent = JSON.parse(editedContent);
      await mutations.updateTemplate.mutateAsync({
        name: selectedTemplate.name,
        content: parsedContent,
      });
      setMode('view');
      // Re-fetch to get the updated template data
      onTemplateCreated?.(selectedTemplate.name);
    } catch (e) {
      alert('Invalid JSON format. Please check your syntax.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setMode('view');
    if (selectedTemplate && 'content' in selectedTemplate) {
      setEditedContent(
        typeof selectedTemplate.content === 'object'
          ? JSON.stringify(selectedTemplate.content, null, 2)
          : ''
      );
    }
  };

  const handleDeleteConfirm = () => {
    if (!selectedTemplate) return;
    mutations.deleteTemplate.mutate(selectedTemplate.name);
    onTemplateChange(null);
  };

  // Determine current mode and content
  const error = mutations.updateTemplate.error || mutations.deleteTemplate.error || mutations.createTemplate.error;

  // Determine the template name to display
  const templateName = mode === 'create' ? newTemplateName : (selectedTemplate?.name || '');
  const displayTitle = mode === 'create' ? 'Create New Template' : (selectedTemplate?.name || 'New Template');

  // Create button group based on mode
  const actionButtons = mode !== 'view' ? (
    <>
      <Button
        onClick={mode === 'create' ? handleCreate : handleSave}
        disabled={
          mode === 'create'
            ? (!newTemplateName.trim() || mutations.createTemplate.isPending)
            : (isSaving || mutations.updateTemplate.isPending)
        }
        size="sm"
      >
        <CheckCircle className="h-4 w-4" />
        {mode === 'create' ? 'Create' : 'Save'}
      </Button>
      <Button onClick={handleCancel} variant="secondary" size="sm">
        <XCircle className="h-4 w-4" />
        Cancel
      </Button>
    </>
  ) : (
    <>
      <Button onClick={handleNew} size="sm">
        <Plus className="h-4 w-4" />
        New
      </Button>
      <Button onClick={() => setMode('edit')} size="sm">
        <Edit3 className="h-4 w-4" />
        Edit
      </Button>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="destructive" size="sm" disabled={mutations.deleteTemplate.isPending}>
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{selectedTemplate?.name}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  return (
    <Card className="lg:col-span-2">
      <CardHeader>
        <div className="flex justify-between">
          <div>
            <h3 className="text-xl font-semibold">{displayTitle}</h3>
            <p className="text-sm text-muted-foreground">
              {mode ==='create' ? 'Define a new configuration template' : 'Configuration Template'}
            </p>
            {selectedTemplate && mode === 'view' && (selectedTemplate.created_by || selectedTemplate.created_at) && (
              <div className="mt-2 space-y-1">
                {selectedTemplate.created_by && (
                  <p className="text-xs text-muted-foreground">
                    Created by: <span className="font-medium">{selectedTemplate.created_by}</span>
                  </p>
                )}
                {selectedTemplate.created_at && (
                  <p className="text-xs text-muted-foreground">
                    Created: <span className="font-medium">{formatDate(selectedTemplate.created_at)}</span>
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="flex gap-2">{actionButtons}</div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <div>
          <Label htmlFor="template-name">Name</Label>
          <Input
            id="template-name"
            value={mode === 'create' ? newTemplateName : templateName}
            onChange={(e) => mode === 'create' && setNewTemplateName(e.target.value)}
            disabled={mode !== 'create'}
            placeholder="template-name"
            autoFocus={mode === 'create'}
          />
        </div>

        <div>
          <Label htmlFor="template-content">Content</Label>
          {mode !== 'view' ? (
            <Textarea
              id="template-content"
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              className="h-96 font-mono"
              placeholder="Enter JSON configuration..."
            />
          ) : (
            <pre className="p-4 rounded-md overflow-auto max-h-96 bg-muted border text-sm">
              <code>{selectedTemplate?.content && JSON.stringify(selectedTemplate.content, null, 2)}</code>
            </pre>
          )}
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{(error as Error).message}</AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
